/**
 * /column-calculator — столбиком: сложение, вычитание, умножение, деление.
 *
 * Ответ школьнику не нужен — калькулятор у него есть. Нужна ЗАПИСЬ: где какая
 * цифра стоит в тетради, что «в уме», что сносим, откуда взялась каждая строка,
 * — и записанная так, как требует ЕГО учитель: уголок в России, скобка в США,
 * «в строку» в Германии, potence во Франции. Поэтому здесь три слоя:
 *   • движок (parse / arith / layout) — трасса и лист клеток, без DOM;
 *   • этот модуль — ввод, проигрыватель шагов, «Реши сам», выдача;
 *   • рендеры одного листа: экран, PNG, PDF, текст.
 *
 * ⚠️ Фразы учителя приходят из движка КЛЮЧАМИ (`st_div_step`) с числами,
 * уже записанными цифрами школы, — переводит страница. Иначе фразы звучали бы
 * по-русски в десяти локалях.
 */
import { parseExample } from './parse.js';
import { build, answerParts, exprText, makeFmt } from './layout.js';
import { SCHOOLS, defaultSchool, groupSample, school as schoolOf } from './schools.js';
import { sheetText } from './text.js';
import { mountSheet } from './render-dom.js';
import { sheetCanvas } from './render-canvas.js';
import { solutionPdf, worksheetPdf } from './render-pdf.js';
import { generate, rng, bumpStats, loadStats } from './practice.js';
import { dec } from './num.js';
import { addTrace } from './arith.js';
import { similarExample } from './similar.js';

const CFG = window.__COL_CFG || {};
const I18N = CFG.i18n || {};
const SLUG = 'column-calculator';
const LOCALE = window.__LOCALE || 'en';
/* Языки браузера: по ним решается запись по умолчанию испанской страницы (schools.js, defaultSchool). */
const NAV_LANGS = (navigator.languages && navigator.languages.length) ? Array.from(navigator.languages) : [navigator.language || ''];
const $ = (id) => document.getElementById(id);
const t = (k) => (I18N[k] != null ? I18N[k] : k);
const RTL = (document.documentElement.getAttribute('dir') || '') === 'rtl';

/*
 * Восточноарабские цифры (٠١٢٣) алгоритм bidi считает «арабскими числами»:
 * в строке с направлением слева направо «١٢٣٤ ÷ ٥٦» показывается как
 * «٥٦ ÷ ١٢٣٤», а фраза «٢ · ٥٦ = ١١٢, ١٢٣ − ١١٢ = ١١» — кашей. Каждое число
 * берём в изолят LRI…PDI: внутри цифры идут старшим разрядом слева (так их
 * пишут и в арабском учебнике), а знаки между числами следуют направлению
 * абзаца — на русской странице слева направо, на арабской справа налево,
 * как читают пример в Машрике. Западным цифрам изолят не нужен.
 */
const EAST_RUN = /[\u0660-\u0669](?:[\u0660-\u0669\u066B,.]*[\u0660-\u0669])?/g;
const bidi = (str) => String(str).replace(EAST_RUN, (m) => '\u2066' + m + '\u2069');

/** Подстановка «:name» — один проход, вставленное повторно не разбирается. */
function fill(tpl, p) {
    return String(tpl).replace(/:([A-Za-z][A-Za-z0-9]*)/g, (m, k) => {
        if (!p || !(k in p)) return m;
        if (k === 'place') return t('place_' + p[k]);
        return p[k];
    });
}

const state = {
    src: '',
    parsed: null,
    school: defaultSchool(LOCALE, NAV_LANGS),
    east: null,
    divMode: 'rem',
    places: 'auto',
    short: false,
    sheet: null,
    ctrl: null,
    step: 0,
    view: 'solve',
    practice: null,
    checked: false,
};

/* ── настройки ────────────────────────────────────────────────────────── */
const TS = () => window.__toolSettings;
function loadSetting(key, def) {
    try { const v = TS() && TS().load ? TS().load(SLUG, key, def) : def; return v == null ? def : v; } catch (_) { return def; }
}
function saveSetting(key, val) {
    try { if (TS() && TS().save) TS().save(SLUG, key, val); } catch (_) { /* настройки — удобство */ }
}

/* ── телеметрия ───────────────────────────────────────────────────────────
   Решённый пример в телеметрию не уходит — уходит его вид: действие, запись, режим деления,
   сколько цифр в числах, есть ли дробная часть. Отвергнутый ввод уходит обрезанным до 60 знаков
   вместе с кодом отказа (tool_diagnostic): это прибор «чего хотели и чего тул не понял».
   Событие шлётся, когда ввод устоялся (человек перестал печатать): «1234 :» посреди набора —
   не отказ и не результат. */
/**
 * Стадии: input (человек начал вводить) → complete (запись показана; ставит __aeToolComplete)
 * либо refused (ввод отвергнут и так оставлен); practice — «Реши сам».
 */
let lastStage = '';
let completedAt = 0;
function stage(name, meta) {
    if (lastStage === name) return;
    // «complete» общий слой ставит с задержкой (склейка 0,8 с): правка в эту секунду ещё идёт в счёт прошлого результата
    if (name === 'input' && Date.now() - completedAt < 1000) return;
    lastStage = name;
    try { if (window.__stage) window.__stage(name, meta || {}); } catch (_) { /* не мешает работе */ }
}
/** Мелкие действия (шаг проигрывателя, цифра в «Реши сам»). */
function ae(action, p) {
    try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: SLUG, action }, p || {})); } catch (_) { /* не мешает работе */ }
}
/** Ключевые действия — в воронку тула: действие, пример, выдача каждого вида, ссылка, настройка, «Реши сам». */
function funnel(name, p) {
    try { if (window.__aeFunnel) window.__aeFunnel(name, p || {}); } catch (_) { /* не мешает работе */ }
}
/** Запись показана — один раз на результат; по полям видно, ЧТО решали, самих чисел нет. */
function complete(p) {
    lastStage = 'complete';       // стадию «complete» ставит сам __aeToolComplete
    completedAt = Date.now();
    try { if (window.__aeToolComplete) window.__aeToolComplete(p); } catch (_) { /* не мешает работе */ }
}
/** Отказ ввода: код, место и сам отвергнутый ввод (до 60 знаков). Один раз на вид отказа и ввод. */
const refusedOnce = new Set();
function refusal(code, p, input) {
    const key = code + '|' + String(input == null ? '' : input);
    if (refusedOnce.has(key)) return;
    refusedOnce.add(key);
    const data = Object.assign({ issue: 'col_' + code }, p || {});
    if (input != null) data.input = String(input).slice(0, 60);
    try { if (window.__aeDiagnostic) window.__aeDiagnostic(data); } catch (_) { /* не мешает работе */ }
}
/** Вид примера без самих чисел: цифр в целой и дробной части каждого числа. */
function shapeOf(r) {
    return {
        op: r.op,
        n: r.nums.length,
        la: r.nums[0].i.length, lb: r.nums[1].i.length,
        frac: r.nums.some((d) => d.f) ? 1 : 0,
    };
}
/**
 * Ввод «устоялся»: человек перестал печатать и смотрит на запись или на отказ. Нажатие
 * (пример-чип, Enter, «ещё такой же», недавний) устаивается сразу.
 */
const SETTLE_MS = 1500;
let settleTimer = 0;
let lastSettled = '';
function settle(ev, from) {
    clearTimeout(settleTimer);
    if (!ev) return;
    const fire = () => {
        if (ev.sig === lastSettled) return;
        lastSettled = ev.sig;
        if (ev.kind === 'ok') {
            complete(Object.assign({ via: 'answer', by: from }, ev.data));
            if (from !== 'sample') pushRecent(ev.raw, ev.data.op, ev.data.mode);
        } else {
            stage('refused', { code: ev.code });
            refusal(ev.code, { at: ev.at, len: ev.len, by: from }, ev.input);
        }
    };
    if (from === 'sample' || from === 'enter' || from === 'again' || from === 'recent' || from === 'op') fire();
    else settleTimer = setTimeout(fire, SETTLE_MS);
}

/* ── школа ────────────────────────────────────────────────────────────── */
function currentSchool() {
    const over = {};
    if (state.east != null && SCHOOLS[state.school] && SCHOOLS[state.school].digits === 'east') over.digits = state.east ? 'east' : 'west';
    return schoolOf(state.school, over);
}
const opSym = (op, S) => ({ add: '+', sub: '−', mul: S.mulSign, div: S.divSign })[op];
const words = () => ({ rem: t('rem_word'), rem_answer: t('rem_answer') });

/*
 * Десятичный знак — свойство ВЫБРАННОЙ записи, а не языка страницы: у
 * испанской страницы по умолчанию мексиканская «casita» с точкой, у испанской
 * «caja» — запятая. Отсюда три вещи: как читать «1.234» в поле (numCtx),
 * какой знак ставить в поле и на чипах (srcText), и какую фразу шага брать —
 * «coma debajo de coma» или «punto debajo de punto». Фраза с другим знаком
 * лежит в переводе под ключом `<ключ>__point` / `<ключ>__comma`; нет такой —
 * берётся основная (она написана под знак школы языка по умолчанию).
 */
const numCtx = (S = currentSchool()) => ({ dec: S.dec, grp: S.grp || '' });
/** Числа, которые пишет сама программа («12.5», без тысяч), — точка всегда десятичная. */
const ASCII = { dec: '.', grp: '' };
const tk = (k, S = currentSchool()) => {
    const alt = I18N[k + (S.dec === '.' ? '__point' : '__comma')];
    return alt != null ? alt : t(k);
};
/** Подпись и значение: «Шаги: …» — двоеточие с пробелами по языку (fr — « : »). */
const labelled = (label) => label + t('label_sep');
/** Запись примера для поля и чипа: «12.5 : 0.25» → знаки и разделитель записи. */
function srcText(nums, op, S = currentSchool()) {
    return nums.map((s) => String(s).replace('.', S.dec)).join(' ' + opSym(op, S) + ' ');
}
const sampleParts = (raw) => raw.split(/\s+[+\-*:]\s+/);
/** «1 : 3 ≈ 0,33», а не «1 : 3 = ≈ 0,33»: знак приближения стоит ВМЕСТО знака равенства. */
const eqJoin = (expr, ans) => expr + (/^\s*≈/.test(ans) ? ' ' : ' = ') + ans;

/* ── разбор и построение ──────────────────────────────────────────────── */
function describeError(e) {
    // Пример групп тысяч («1.000.000») — знаком выбранной записи.
    const base = e.code === 'bad_group' ? groupSample(t('err_' + e.code), currentSchool()) : t('err_' + e.code);
    const at = e.at != null ? ' (' + fill(t('err_at'), { n: String(e.at + 1) }) + ')' : '';
    const ch = e.ch && e.code === 'unexpected_char' ? ' ' + fill(t('ch_quote'), { ch: e.ch }) : '';
    return base + ch + at;
}

function recompute(opts = {}) {
    state.src = $('colExpr').value;
    let r = parseExample(state.src, numCtx());
    const err = $('colErr');
    const hint = $('colHint');
    const expr = $('colExpr');
    err.hidden = true;
    hint.hidden = true;
    expr.removeAttribute('aria-invalid');
    $('colExprWrap').classList.remove('bad');
    if (!r) {
        showEmpty(t('empty_state'));
        if (!opts.keepFields) renderNums(null);
        saveHash();
        settle(null);
        return;
    }
    const from = opts.from || (opts.fromInput ? 'typed' : '');
    if (r.error) {
        err.textContent = describeError(r.error);
        err.hidden = false;
        expr.setAttribute('aria-invalid', 'true');
        $('colExprWrap').classList.add('bad');
        showEmpty(t('empty_fix'));
        saveHash();          // ссылка и перезагрузка возвращают то, что набрано, а не прошлый пример
        if (from) {
            stage('input', {});
            settle({ kind: 'refused', sig: 'x|' + r.error.code + '|' + state.src, code: r.error.code, at: r.error.at, len: state.src.length, input: state.src }, from);
        }
        return;
    }
    if (!opts.keepFields) renderNums(r);
    syncOp(r.op);
    // «345 + 204 + » — знак в конце ждёт следующее слагаемое: пока его нет, на листе остаётся сумма
    // уже набранных чисел, а не пустое место с просьбой «допишите второе число».
    if (r.incomplete && r.op === 'add' && r.nums && r.nums.length >= 2) r = { op: r.op, nums: r.nums, spans: r.spans, notes: [], open: true };
    if (r.incomplete) {
        hint.textContent = r.op ? t('hint_need_second') : t('hint_need_op');
        hint.hidden = false;
        showEmpty(t('empty_state'));
        settle(null);
        return;
    }
    state.parsed = r;
    const S = currentSchool();
    const mode = r.op === 'div' ? state.divMode : undefined;
    try {
        state.sheet = build(r, { school: S, mode, places: state.places, short: state.short, words: words() });
    } catch (e) {
        // Не отказ ввода, а наша ошибка: в телеметрию — место и текст, человеку — фраза у поля.
        showEmpty(t('empty_fix'));
        err.textContent = t('err_internal');
        err.hidden = false;
        if (window.__handleError) window.__handleError(e, 'col_build', { say: t('err_internal') });
        else console.error('[column-calculator]', e);
        return;
    }
    state.checked = false;
    state.stepped = false;
    renderSheet();
    renderAnswer();
    saveHash();
    if (from) {
        stage('input', { op: r.op });
        const data = Object.assign(shapeOf(r), { school: state.school, steps: state.sheet.steps.length });
        if (r.op === 'div') { data.mode = state.divMode; data.places = String(state.places); data.short = state.short ? 1 : 0; }
        const raw = r.nums.map((d) => d.i + (d.f ? '.' + d.f : '')).join(' ' + ({ add: '+', sub: '-', mul: '*', div: ':' })[r.op] + ' ');
        settle({ kind: 'ok', sig: raw + '|' + state.school + '|' + (r.op === 'div' ? state.divMode + state.places + state.short : ''), data, raw }, from);
    }
    if (state.view === 'practice' && !opts.keepPractice) startPractice(null);
}

function showEmpty(msg) {
    state.parsed = null;
    state.sheet = null;
    $('colAnswer').hidden = true;
    $('colStepsPanel').hidden = true;
    $('colCheckPanel').hidden = true;
    $('colPlayer').hidden = true;
    $('colSayWrap').hidden = true;                          // пустая плашка фразы над пустым листом
    document.querySelector('#colApp .col-keyshint').hidden = true;
    $('colSay').textContent = '';
    const host = $('colSheet');
    host.textContent = '';
    const p = document.createElement('p');
    p.className = 'col-empty';
    p.textContent = msg;
    host.appendChild(p);
}

/* ── поля чисел рядом с выражением ─────────────────────────────────────── */
function renderNums(r) {
    const box = $('colNums');
    const op = (r && r.op) || state.opHint || 'div';
    const raws = r && r.spans ? r.spans.map(([a, b]) => state.src.slice(a, b)) : [];
    // «345 + 204 + » — знак в конце ждёт ещё одно число: под него нужно пустое поле. Без этого кнопка
    // «добавить слагаемое» дописывала знак в строку, а поля оставалось два — нажатие выглядело мёртвым.
    const dangling = !!(r && r.incomplete && r.op && raws.length && state.src.slice(r.spans[r.spans.length - 1][1]).trim());
    const n = op === 'add' ? Math.max(2, Math.min(5, (raws.length + (dangling ? 1 : 0)) || 2)) : 2;
    const S = currentSchool();
    box.textContent = '';
    for (let k = 0; k < n; k++) {
        if (k) {
            const s = document.createElement('span');
            s.className = 'col-numop';
            s.setAttribute('aria-hidden', 'true');
            s.textContent = opSym(op, S);
            box.appendChild(s);
        }
        const inp = document.createElement('input');
        inp.type = 'text';
        inp.className = 'tc-in col-num';
        inp.setAttribute('inputmode', 'decimal');
        inp.setAttribute('enterkeyhint', k < n - 1 ? 'next' : 'done');
        inp.setAttribute('autocomplete', 'off');
        inp.setAttribute('spellcheck', 'false');
        inp.setAttribute('aria-label', t('num_' + op + '_' + Math.min(k, 1)));
        inp.value = raws[k] || '';
        inp.addEventListener('input', onNumInput);
        inp.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                const all = box.querySelectorAll('.col-num');
                if (all[k + 1]) all[k + 1].focus();
                else inp.blur();
            }
        });
        box.appendChild(inp);
    }
    $('colAddNum').hidden = !(op === 'add' && n < 5);
}

function onNumInput() {
    const vals = Array.from(document.querySelectorAll('#colNums .col-num')).map((i) => i.value.trim());
    const op = (state.parsed && state.parsed.op) || state.opHint || 'div';
    const S = currentSchool();
    const filled = vals.filter(Boolean);
    const sym = ' ' + opSym(op, S) + ' ';
    $('colExpr').value = filled.length > 1 ? filled.join(sym) : (filled.length ? filled[0] + sym : '');
    schedule('fields', true);
}

function syncOp(op) {
    if (op) state.opHint = op;
    document.querySelectorAll('[data-col-op]').forEach((b) => {
        const on = b.getAttribute('data-col-op') === (op || state.opHint);
        b.classList.toggle('on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    const isDiv = (op || state.opHint) === 'div';
    $('colDivOpts').hidden = !isDiv;
    document.querySelectorAll('[data-col-sample-op]').forEach((c) => {
        c.hidden = c.getAttribute('data-col-sample-op') !== (op || state.opHint);
    });
}

/** Кнопка действия: меняет действие примера или дописывает знак к первому числу. */
function pickOp(op) {
    const S = currentSchool();
    const r = parseExample($('colExpr').value, numCtx());
    state.opHint = op;
    let raws = [];
    if (r && !r.error && r.spans) raws = r.spans.map(([a, b]) => $('colExpr').value.slice(a, b));
    if (raws.length >= 2) {
        const keep = op === 'add' ? raws : raws.slice(0, 2);
        $('colExpr').value = keep.join(' ' + opSym(op, S) + ' ');
    } else if (raws.length === 1) {
        $('colExpr').value = raws[0] + ' ' + opSym(op, S) + ' ';
        focusEnd($('colExpr'));
    } else {
        // Пусто или непонятно: показываем пример этого действия.
        const chip = document.querySelector('[data-col-sample-op="' + op + '"]');
        if (chip) applySample(chip);
        return;
    }
    funnel('mode', { op });
    recompute({ from: 'op' });
}

function focusEnd(el) {
    try { el.focus({ preventScroll: true }); const n = el.value.length; el.setSelectionRange(n, n); } catch (_) { /* поле без выделения */ }
}

let timer = 0;
function schedule(from, keepFields) {
    clearTimeout(timer);
    timer = setTimeout(() => recompute({ from: from === true ? 'typed' : from, keepFields }), 250);
}

/* ── лист и шаги ──────────────────────────────────────────────────────── */
function phrase(step) {
    const sh = state.sheet;
    if (step.answer) return fill(t('st_answer'), { ans: answerText() });
    return step.f.map((fr) => fill(tk(fr.k, sh.school), fr.p)).join(' ');
}

function answerText() {
    return answerParts(state.sheet, words()).text;
}

function renderSheet() {
    const sh = state.sheet;
    state.ctrl = mountSheet($('colSheet'), sh, { cellLabel: t('p_cell') });
    $('colSheet').setAttribute('aria-label', labelled(t('sheet_label')) + exprText(sh.ex, sh.school));
    const list = $('colSteps');
    list.textContent = '';
    sh.steps.forEach((st, i) => {
        const li = document.createElement('li');
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'col-stepb';
        b.textContent = bidi(phrase(st));
        b.addEventListener('click', () => { go(i); ae('step_pick', {}); });
        li.appendChild(b);
        list.appendChild(li);
    });
    $('colStepsPanel').hidden = state.view !== 'solve';
    $('colPlayer').hidden = state.view !== 'solve';
    $('colSayWrap').hidden = state.view !== 'solve';
    document.querySelector('#colApp .col-keyshint').hidden = false;
    $('colCheckPanel').hidden = true;
    $('colCheckSheets').textContent = '';
    go(sh.steps.length - 1, true);
    renderPrintAnswer();
}

function go(k, quiet) {
    const sh = state.sheet;
    if (!sh || !state.ctrl) return;
    state.step = state.ctrl.setStep(k);
    const last = sh.steps.length - 1;
    $('colStepNo').textContent = fill(t('step_of'), { k: String(state.step + 1), n: String(last + 1) });
    $('colPrev').disabled = state.step <= 0;
    $('colFirst').disabled = state.step <= 0;
    $('colNext').disabled = state.step >= last;
    $('colAll').disabled = state.step >= last;
    $('colSay').textContent = state.view === 'solve' ? bidi(phrase(sh.steps[state.step])) : '';
    document.querySelectorAll('#colSteps li').forEach((li, i) => {
        li.classList.toggle('on', i === state.step);
        li.classList.toggle('done', i < state.step);
        const b = li.firstChild;
        if (i === state.step) b.setAttribute('aria-current', 'step'); else b.removeAttribute('aria-current');
    });
    const cur = document.querySelector('#colSteps li.on');
    if (cur && !quiet) {
        // Прокрутка ВНУТРИ списка шагов, страница не двигается.
        const box = $('colSteps');
        const top = cur.offsetTop - box.offsetTop;
        if (top < box.scrollTop || top + cur.offsetHeight > box.scrollTop + box.clientHeight) box.scrollTop = Math.max(0, top - 8);
    }
    if (!quiet && !state.stepped) { state.stepped = true; funnel('steps', { op: sh.op, n: sh.steps.length }); }
}

/* ── ответ ────────────────────────────────────────────────────────────── */
function renderAnswer() {
    const sh = state.sheet;
    const S = sh.school;
    const box = $('colAnswer');
    box.hidden = false;
    // Пример и ответ пишутся слева направо; арабская страница с восточными
    // цифрами читает их справа налево — как в своём учебнике.
    const dir = RTL && S.digits === 'east' ? 'rtl' : 'ltr';
    $('colAnsExpr').dir = dir;
    $('colAnsExpr').textContent = bidi(exprText(sh.ex, S) + (sh.answer.approx ? '' : ' ='));
    const val = $('colAnsVal');
    val.dir = dir;
    val.textContent = '';
    const ap = answerParts(sh, words());
    for (const part of ap.parts) {
        if (part.t === 'txt') { val.appendChild(document.createTextNode(bidi(part.v))); continue; }
        const span = document.createElement('span');
        span.className = 'col-per per-' + S.period;
        if (S.period === 'paren') span.textContent = '(' + part.v + ')';
        else if (S.period === 'dots') {
            Array.from(part.v).forEach((ch, i, arr) => {
                const d = document.createElement('span');
                d.textContent = ch;
                if (i === 0 || i === arr.length - 1) d.className = 'pd';
                span.appendChild(d);
            });
        } else span.textContent = part.v;
        val.appendChild(span);
    }
    const notes = [];
    if (state.parsed.notes && state.parsed.notes.indexOf('grouping') >= 0) notes.push(tk('note_grouping', S));
    const tr = sh.traceObj;
    if (tr && tr.notes && tr.notes.indexOf('rem_needs_int') >= 0 && state.divMode === 'rem') notes.push(t('note_rem_int'));
    if (sh.answer.period) notes.push(fill(t('note_period'), { n: makeFmt(S).dg(String(sh.answer.period.len)) }));
    if (sh.answer.cut) notes.push(t('note_cut'));
    $('colAnsNote').textContent = bidi(notes.join(' '));
    $('colAnsNote').hidden = !notes.length;
    const more = sh.op === 'div' && tr && tr.mode === 'rem' && tr.remainder !== 0n;
    $('colMore').hidden = !more;
    box.classList.remove('tc-flash');
    void box.offsetWidth;
    box.classList.add('tc-flash');
}

function renderPrintAnswer() {
    const el = $('colPrintAns');
    if (!el || !state.sheet) return;
    el.textContent = bidi(eqJoin(exprText(state.sheet.ex, state.sheet.school), answerText()));
}

/* ── проверка обратным действием ──────────────────────────────────────── */
function checkPlan() {
    const sh = state.sheet;
    const S = sh.school;
    const ex = sh.ex;
    const fmtD = (d) => d.i + (d.f ? '.' + d.f : '');
    const P = (a, op, b) => parseExample(fmtD(a) + ' ' + ({ add: '+', sub: '-', mul: '*', div: ':' })[op] + ' ' + fmtD(b), ASCII);
    const res = sh.traceObj.result;
    if (ex.op === 'div') {
        const tr = sh.traceObj;
        if (tr.period || tr.cut || (tr.places !== 'auto' && sh.answer.approx)) return { none: t('check_none') };
        const q = tr.mode === 'rem' ? dec(tr.result.i, '') : tr.result;
        const b1 = tr.b1;
        const out = [{ title: t('check_mul'), ex: P(q, 'mul', b1) }];
        if (tr.mode === 'rem' && tr.remainder !== 0n) {
            const prod = { i: (BigInt(q.i) * BigInt(b1.i)).toString(), f: '' };
            out.push({ title: t('check_add_rem'), ex: P(prod, 'add', dec(tr.remainder.toString(), '')) });
        }
        out.target = tr.a1;
        return out;
    }
    if (ex.op === 'mul') {
        if (/^0*$/.test(ex.nums[0].i + ex.nums[0].f) || /^0*$/.test(ex.nums[1].i + ex.nums[1].f)) return { none: t('check_none_zero') };
        const out = [{ title: t('check_div'), ex: P(res, 'div', ex.nums[1]) }];
        out.target = ex.nums[0];
        return out;
    }
    if (ex.op === 'add') {
        const last = ex.nums[ex.nums.length - 1];
        const out = [{ title: t('check_sub'), ex: P(res, 'sub', last) }];
        // Цель — сумма остальных слагаемых (для двух — просто первое).
        out.target = ex.nums.length === 2 ? ex.nums[0] : addTrace(ex.nums.slice(0, -1)).result;
        return out;
    }
    // вычитание: разность + вычитаемое = уменьшаемое
    const tr = sh.traceObj;
    const out = [{ title: t('check_add'), ex: P(tr.result, 'add', tr.b) }];
    out.target = tr.a;
    void S;
    return out;
}

function showCheck() {
    if (!state.sheet) return;
    const panel = $('colCheckPanel');
    const box = $('colCheckSheets');
    box.textContent = '';
    panel.hidden = false;
    const plan = checkPlan();
    if (plan.none) {
        const p = document.createElement('p');
        p.className = 'tc-note';
        p.textContent = plan.none;
        box.appendChild(p);
        return;
    }
    const S = currentSchool();
    let lastSheet = null;
    for (const item of plan) {
        if (!item.ex || item.ex.error || item.ex.incomplete) continue;
        const sh = build(item.ex, { school: S, mode: 'dec', places: 'auto', words: words() });
        lastSheet = sh;
        const fig = document.createElement('figure');
        fig.className = 'col-checkfig';
        const cap = document.createElement('figcaption');
        cap.textContent = bidi(labelled(item.title) + eqJoin(exprText(sh.ex, S), answerParts(sh, words()).text));
        const host = document.createElement('div');
        host.className = 'col-sheet col-sheet-sm';
        fig.appendChild(cap);
        fig.appendChild(host);
        box.appendChild(fig);
        mountSheet(host, sh, {}).setStep(sh.steps.length - 1);
    }
    if (lastSheet && plan.target) {
        const got = lastSheet.traceObj.result;
        const same = (a, b) => {
            const k = Math.max(a.f.length, b.f.length);
            const pad = (d) => BigInt(d.i + d.f + '0'.repeat(k - d.f.length));
            return pad(a) === pad(b);
        };
        const ok = same(got, plan.target);
        const p = document.createElement('p');
        p.className = 'tc-msg ' + (ok ? 'good' : 'bad');
        p.textContent = ok ? t('check_ok') : t('check_bad');
        box.appendChild(p);
    }
    state.checked = true;
    funnel('check', { op: state.sheet.op });
}

/* ── выдача ───────────────────────────────────────────────────────────── */
function fileBase() {
    const sh = state.sheet;
    const raw = sh.ex.nums.map((d) => d.i + (d.f ? ',' + d.f : '')).join(({ add: '+', sub: '-', mul: 'x', div: '÷' })[sh.op]);
    let base = t('file_prefix') + '-' + raw;
    try { if (window.__outputName) base = window.__outputName.sanitize(base); } catch (_) { /* имя ниже */ }
    return base.replace(/[\\/:*?"<>|]+/g, '_').slice(0, 60);
}
function fileName(ext) {
    try { if (window.__outputName) return window.__outputName.join(fileBase(), ext); } catch (_) { /* имя ниже */ }
    return fileBase() + '.' + ext;
}

function solutionText() {
    const sh = state.sheet;
    const lines = [eqJoin(exprText(sh.ex, sh.school), answerText()), '', sheetText(sh), '', labelled(t('steps_title')).trimEnd()];
    lines[0] = bidi(lines[0]);
    sh.steps.forEach((st, i) => { if (!st.answer) lines.push((i + 1) + '. ' + bidi(phrase(st))); });
    lines.push(bidi(fill(t('st_answer'), { ans: answerText() })));
    return lines.join('\n');
}

async function copyText(text) {
    try {
        if (window.__copyText) return !!(await window.__copyText(text));
        await navigator.clipboard.writeText(text);
        return true;
    } catch (_) { return false; }
}

async function doCopy() {
    if (!state.sheet) return;
    const ok = await copyText(solutionText());
    if (window.__toast) window.__toast(ok ? t('copied') : t('copy_failed'), ok ? 'success' : 'error');
    funnel('export', { kind: 'copy', op: state.sheet.op, school: state.school, ok: ok ? 1 : 0 });
}

/* The canvas is encoded by the site's one encoder (timbrica-core.js window.__canvasBlob):
   it always answers — toBlob, then OffscreenCanvas, then a data URL, or a rejection
   (CANVAS_ENCODE_FAILED) that lands in the catch below with the drawing errors. */
async function doPng() {
    if (!state.sheet) return;
    const sh = state.sheet;
    const btn = $('colPng');
    btn.disabled = true;
    try {
        const cv = sheetCanvas(sh, { title: bidi(exprText(sh.ex, sh.school)), answer: bidi(t('answer_label') + ' ' + answerText()), rtl: RTL });
        const blob = await window.__canvasBlob(cv, 'image/png');
        if (window.__downloadFile) window.__downloadFile(blob, fileName('png'));
        funnel('export', { kind: 'png', op: sh.op, school: state.school, ok: 1, w: cv.width, h: cv.height });
    } catch (e) {
        if (window.__handleError) window.__handleError(e, 'png_export', { say: t('png_failed') });
        funnel('export', { kind: 'png', op: sh.op, school: state.school, ok: 0 });
    } finally {
        btn.disabled = false;
    }
}

async function doPdf() {
    if (!state.sheet) return;
    const sh = state.sheet;
    const btn = $('colPdf');
    btn.disabled = true;
    const t0 = performance.now();
    try {
        const bytes = await solutionPdf({
            sheet: sh,
            title: exprText(sh.ex, sh.school),
            answer: t('answer_label') + ' ' + answerText(),
            stepsTitle: t('steps_title'),
            steps: sh.steps.filter((s) => !s.answer).map(phrase),
            footer: CFG.site || '',
            pdfLib: CFG.pdfLib,
            rtl: RTL,
        });
        if (window.__downloadFile) window.__downloadFile(new Blob([bytes], { type: 'application/pdf' }), fileName('pdf'));
        funnel('export', { kind: 'pdf', op: sh.op, school: state.school, ok: 1, ms: Math.round(performance.now() - t0) });
    } catch (e) {
        if (window.__handleError) window.__handleError(e, 'pdf_export', { say: t('pdf_failed') });
        else if (window.__toast) window.__toast(t('pdf_failed'), 'error');
        funnel('export', { kind: 'pdf', op: sh.op, school: state.school, ok: 0 });
    } finally {
        btn.disabled = false;
    }
}

function doPrint() {
    if (!state.sheet) return;
    go(state.sheet.steps.length - 1, true);
    funnel('export', { kind: 'print', op: state.sheet.op, school: state.school });
    window.print();
}

/* ── ссылка ───────────────────────────────────────────────────────────── */
function hashParams() {
    const q = new URLSearchParams();
    q.set('e', state.src.trim());
    q.set('s', state.school);
    if (state.parsed && state.parsed.op === 'div') {
        q.set('d', state.divMode);
        if (state.places !== 'auto') q.set('n', String(state.places));
        if (state.short) q.set('k', '1');
    }
    if (state.east != null) q.set('ea', state.east ? '1' : '0');
    return q;
}
function saveHash() {
    try { history.replaceState(null, '', '#' + hashParams().toString()); } catch (_) { /* песочница */ }
}
function loadHash() {
    const h = String(location.hash || '').replace(/^#/, '');
    if (!h) return false;
    const q = new URLSearchParams(h);
    const e = q.get('e');
    if (!e) return false;
    if (q.get('s') && SCHOOLS[q.get('s')]) state.school = q.get('s');
    if (q.get('d') === 'rem' || q.get('d') === 'dec') state.divMode = q.get('d');
    const n = q.get('n');
    state.places = n && /^\d$/.test(n) ? +n : 'auto';
    state.short = q.get('k') === '1';
    if (q.get('ea') === '1' || q.get('ea') === '0') state.east = q.get('ea') === '1';
    $('colExpr').value = e;
    return true;
}
async function doShare() {
    if (!state.sheet) return;
    saveHash();
    const url = location.href;
    if (navigator.share) {
        try {
            await navigator.share({ title: document.title, text: exprText(state.sheet.ex, state.sheet.school), url });
            funnel('share', { via: 'native' });
            return;
        } catch (e) {
            if (e && e.name === 'AbortError') return;
        }
    }
    const ok = await copyText(url);
    if (window.__toast) window.__toast(ok ? t('link_copied') : t('copy_failed'), ok ? 'success' : 'error');
    funnel('share', { via: 'clipboard', ok: ok ? 1 : 0 });
}

/* ── «Реши сам» ───────────────────────────────────────────────────────── */
function setView(v) {
    state.view = v;
    document.querySelectorAll('[data-col-view]').forEach((b) => {
        const on = b.getAttribute('data-col-view') === v;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
    });
    $('colPracticeBar').hidden = v !== 'practice';
    $('colPlayer').hidden = v !== 'solve' || !state.sheet;
    $('colStepsPanel').hidden = v !== 'solve' || !state.sheet;
    $('colSayWrap').hidden = v !== 'solve' || !state.sheet;
    $('colApp').classList.toggle('is-practice', v === 'practice');
    if (v === 'practice') {
        if (!state.practice || state.practice.sheet !== state.sheet) startPractice(null);
        funnel('view', { v });
    } else {
        state.practice = null;
        if (state.sheet) renderSheet();
    }
}

function startPractice(gen) {
    const sh = state.sheet;
    if (!sh) { $('colPMsg').textContent = t('p_nothing'); return; }
    const order = sh.cells.filter((c) => c.o != null).sort((a, b) => a.o - b.o).map((c) => c.id);
    state.practice = {
        sheet: sh, order, typed: new Map(), ok: new Set(), bad: new Set(), hinted: new Set(),
        cur: order[0], mistakes: 0, hints: 0, done: false, gen,
    };
    state.ctrl = mountSheet($('colSheet'), sh, { cellLabel: t('p_cell') });
    state.ctrl.setPractice(state.practice);
    bindPracticeCells();
    pmsg(t('p_intro'), '');
    renderStats();
    stage('practice', { op: sh.op });
    funnel('practice_start', { op: sh.op, level: gen ? gen.level : 0, own: gen ? 0 : 1 });
}

function bindPracticeCells() {
    state.ctrl.paper.addEventListener('click', (e) => {
        const el = e.target.closest('.col-c');
        if (!el || !state.practice) return;
        const id = +el.dataset.id;
        if (state.practice.order.indexOf(id) < 0) return;
        state.practice.cur = id;
        state.ctrl.setPractice(state.practice);
        $('colSheetWrap').focus({ preventScroll: true });
    });
}

function pmsg(text, cls) {
    const m = $('colPMsg');
    m.textContent = bidi(text);
    m.className = 'col-pmsg' + (cls ? ' ' + cls : '');
}

function cellOf(id) { return state.practice.sheet.cells[id]; }

function advance() {
    const p = state.practice;
    const i = p.order.indexOf(p.cur);
    for (let k = i + 1; k < p.order.length; k++) {
        const id = p.order[k];
        if (!p.ok.has(id) && !(p.typed.has(id) && !p.instant)) { p.cur = id; return; }
    }
    const next = p.order.find((id) => !p.typed.has(id) || p.bad.has(id));
    if (next != null) p.cur = next;
}

function typeDigit(d) {
    const p = state.practice;
    if (!p || p.done || p.cur == null) return;
    const id = p.cur;
    const c = cellOf(id);
    p.typed.set(id, d);
    p.instant = $('colPInstant').checked;
    if (p.instant) {
        if (d === c.ch) {
            p.ok.add(id);
            p.bad.delete(id);
            pmsg('', '');
            advance();
        } else {
            p.bad.add(id);
            p.ok.delete(id);
            p.mistakes++;
            pmsg(t('p_wrong_digit'), 'bad');
            ae('practice_wrong', { op: p.sheet.op });
        }
    } else {
        p.bad.delete(id);
        advance();
    }
    state.ctrl.setPractice(p);
    maybeDone();
}

function erase() {
    const p = state.practice;
    if (!p || p.done) return;
    if (p.typed.has(p.cur) && !p.hinted.has(p.cur)) {
        p.typed.delete(p.cur);
        p.ok.delete(p.cur);
        p.bad.delete(p.cur);
    } else {
        const i = p.order.indexOf(p.cur);
        if (i > 0) p.cur = p.order[i - 1];
    }
    state.ctrl.setPractice(p);
}

function moveCur(delta) {
    const p = state.practice;
    if (!p) return;
    const i = p.order.indexOf(p.cur);
    const k = Math.max(0, Math.min(p.order.length - 1, i + delta));
    p.cur = p.order[k];
    state.ctrl.setPractice(p);
}

function checkPractice() {
    const p = state.practice;
    if (!p) return;
    let wrong = 0;
    let empty = 0;
    for (const id of p.order) {
        if (!p.typed.has(id)) { empty++; continue; }
        if (p.typed.get(id) === cellOf(id).ch) { p.ok.add(id); p.bad.delete(id); } else { p.bad.add(id); p.ok.delete(id); wrong++; }
    }
    p.mistakes += wrong;
    state.ctrl.setPractice(p);
    if (wrong) pmsg(fill(t('p_errors'), { n: String(wrong) }), 'bad');
    else if (empty) pmsg(t('p_fill_all'), '');
    funnel('practice_check', { wrong, empty });
    maybeDone();
}

function hint() {
    const p = state.practice;
    if (!p || p.done) return;
    const sh = p.sheet;
    // Первый шаг, в котором есть незаполненная или неверная клетка.
    let target = -1;
    for (let k = 0; k < sh.steps.length && target < 0; k++) {
        for (const c of sh.cells) {
            if (c.o == null || c.s !== k) continue;
            if (!p.ok.has(c.id)) { target = k; break; }
        }
    }
    if (target < 0) return;
    for (const c of sh.cells) {
        if (c.o == null || c.s !== target || p.ok.has(c.id)) continue;
        p.typed.set(c.id, c.ch);
        p.ok.add(c.id);
        p.bad.delete(c.id);
        p.hinted.add(c.id);
    }
    p.hints++;
    const next = p.order.find((id) => !p.ok.has(id));
    if (next != null) p.cur = next;
    state.ctrl.setPractice(p);
    pmsg(phrase(sh.steps[target]), 'hint');
    funnel('practice_hint', { op: sh.op, n: p.hints });
    maybeDone();
}

function maybeDone() {
    const p = state.practice;
    if (!p || p.done) return;
    const all = p.order.every((id) => p.typed.get(id) === cellOf(id).ch);
    if (!all) return;
    p.done = true;
    p.order.forEach((id) => { p.ok.add(id); p.bad.delete(id); });
    p.cur = null;
    state.ctrl.setPractice(p);
    const clean = p.mistakes === 0 && p.hints === 0;
    const key = p.gen ? p.gen.op : p.sheet.op;
    const lvl = p.gen ? p.gen.level : 0;
    bumpStats(key, lvl, clean);
    renderStats();
    pmsg((clean ? t('p_done_clean') : t('p_done')) + ' ' + fill(t('st_answer'), { ans: answerText() }), 'good');
    funnel('practice_done', { op: key, level: lvl, clean: clean ? 1 : 0, hints: p.hints, mistakes: p.mistakes, cells: p.order.length });
}

function renderStats() {
    const s = loadStats();
    const el = $('colPStats');
    if (!s.total) { el.hidden = true; return; }
    el.hidden = false;
    el.textContent = fill(t('p_stats'), { n: String(s.total), c: String(s.cleanTotal || 0) });
}

function newExample() {
    const op = $('colPOp').value;
    const level = +$('colPLevel').value;
    const R = rng((Date.now() ^ (Math.random() * 1e9)) >>> 0);
    const g = generate(op, level, R);
    applyGenerated(g);
    startPractice({ op, level });
}

function applyGenerated(g) {
    $('colExpr').value = srcText(g.nums, g.op);
    if (g.mode) setDivMode(g.mode, true);
    recompute({ keepPractice: true });
}

/* ── лист примеров для печати ─────────────────────────────────────────── */
async function makeWorksheet() {
    const op = $('colWsOp').value;
    const level = +$('colWsLevel').value;
    const count = Math.max(2, Math.min(24, +$('colWsCount').value || 8));
    const vars = Math.max(1, Math.min(4, +$('colWsVariants').value || 1));
    const withKey = $('colWsKey').checked;
    const S = currentSchool();
    const seed = (Date.now() >>> 0) ^ 0x5bd1e995;
    const variants = [];
    for (let v = 0; v < vars; v++) {
        const R = rng(seed + v * 7919);
        const seen = new Set();
        const list = [];
        const ops = op === 'mix' ? ['add', 'sub', 'mul', 'div'] : [op];
        for (let j = 0; list.length < count && j < count * 30; j++) {
            const g = generate(ops[list.length % ops.length], level, R);
            const key = g.op + g.nums.join(',');
            if (seen.has(key)) continue;
            seen.add(key);
            const ex = parseExample(g.nums.join(' ' + ({ add: '+', sub: '-', mul: '*', div: ':' })[g.op] + ' '), ASCII);
            list.push(build(ex, { school: S, mode: g.mode || 'rem', places: 'auto', short: state.short, words: words() }));
        }
        variants.push(list);
    }
    const btn = $('colWsMake');
    btn.disabled = true;
    try {
        const bytes = await worksheetPdf({
            variants, withKey, pdfLib: CFG.pdfLib, rtl: RTL, footer: CFG.site || '',
            labels: {
                heading: t('ws_heading'), name: t('ws_name'), task: t('ws_task'), key: t('ws_key'), sep: t('label_sep'),
                variant: (n) => fill(t('ws_variant'), { n: String(n) }),
            },
        });
        const name = (() => { try { return window.__outputName ? window.__outputName.join(t('ws_file'), 'pdf') : t('ws_file') + '.pdf'; } catch (_) { return 'worksheet.pdf'; } })();
        if (window.__downloadFile) window.__downloadFile(new Blob([bytes], { type: 'application/pdf' }), name);
        funnel('export', { kind: 'worksheet', op, level, count, variants: vars, key: withKey ? 1 : 0, school: state.school, ok: 1 });
    } catch (e) {
        if (window.__handleError) window.__handleError(e, 'worksheet_export', { say: t('ws_failed') });
        else if (window.__toast) window.__toast(t('ws_failed'), 'error');
        funnel('export', { kind: 'worksheet', op, level, ok: 0 });
    } finally {
        btn.disabled = false;
    }
}

/* ── чипы-примеры ─────────────────────────────────────────────────────── */
function applySample(chip) {
    const raw = chip.getAttribute('data-col-sample');           // «1234 : 56» с точкой в дробях
    const op = chip.getAttribute('data-col-sample-op');
    const mode = chip.getAttribute('data-col-sample-mode');
    $('colExpr').value = srcText(sampleParts(raw), op);
    if (mode) setDivMode(mode, true);
    funnel('sample', { op });
    recompute({ from: 'sample' });
}

/* ── «ещё такой же пример» ─────────────────────────────────────────────── */
/** Те же действие, число цифр и нули на тех же местах — другие цифры (similar.js). */
function again() {
    let base = state.parsed;
    if (!base) {
        // Поле пустое или с ошибкой — образцом служит первый пример выбранного действия.
        const chip = document.querySelector('[data-col-sample-op="' + (state.opHint || 'div') + '"]');
        const r = chip ? parseExample(chip.getAttribute('data-col-sample'), ASCII) : null;
        if (r && !r.error && !r.incomplete) base = r;
    }
    const g = base ? similarExample(base, rng((Date.now() ^ (Math.random() * 1e9)) >>> 0)) : null;
    funnel('again', { op: base ? base.op : '', ok: g ? 1 : 0 });
    if (!g) return;
    $('colExpr').value = srcText(g.nums, g.op);
    recompute({ from: 'again' });
}

/* ── недавние примеры ──────────────────────────────────────────────────── */
/**
 * Последние решённые примеры — чипами под готовыми: домашнее задание решают подряд, и к
 * прошлому примеру возвращаются. Хранятся в этом браузере записью с точкой и знаком ASCII
 * («12.5 : 0.25») — показываются знаками выбранной записи, как готовые примеры.
 */
const RECENT_MAX = 6;
let recent = [];
function loadRecent() {
    const v = loadSetting('recent', []);
    recent = Array.isArray(v) ? v.filter((x) => x && typeof x.raw === 'string' && /^(add|sub|mul|div)$/.test(x.op)).slice(0, RECENT_MAX) : [];
}
function paintRecent() {
    const box = $('colRecent');
    if (!box) return;
    const samples = new Set(Array.from(document.querySelectorAll('[data-col-sample]')).map((c) => c.getAttribute('data-col-sample')));
    const list = recent.filter((x) => !samples.has(x.raw));
    box.hidden = !list.length;
    const host = $('colRecentList');
    host.textContent = '';
    list.forEach((x) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'tc-chip col-mono';
        b.dir = 'ltr';
        b.setAttribute('data-col-recent', x.raw);
        b.setAttribute('data-col-recent-op', x.op);
        if (x.mode) b.setAttribute('data-col-recent-mode', x.mode);
        b.textContent = bidi(srcText(sampleParts(x.raw), x.op));
        host.appendChild(b);
    });
}
function pushRecent(raw, op, mode) {
    if (!raw || raw.length > 70) return;
    // «1234 : 5», на котором человек задумался, а потом дописал «6», — не отдельный пример: начало уходит
    recent = [{ raw, op, mode: mode || '' }].concat(recent.filter((x) => x.raw !== raw && raw.indexOf(x.raw) !== 0)).slice(0, RECENT_MAX);
    saveSetting('recent', recent);
    paintRecent();
}

/* ── переключатели ────────────────────────────────────────────────────── */
function setDivMode(m, quiet) {
    state.divMode = m === 'dec' ? 'dec' : 'rem';
    document.querySelectorAll('[data-col-mode]').forEach((b) => {
        const on = b.getAttribute('data-col-mode') === state.divMode;
        b.classList.toggle('on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    $('colPlacesWrap').hidden = state.divMode !== 'dec';
    saveSetting('divMode', state.divMode);
    if (!quiet) { funnel('setting', { k: 'div_mode', v: state.divMode }); recompute(); }
}
function setShort(v, quiet) {
    state.short = !!v;
    document.querySelectorAll('[data-col-short]').forEach((b) => {
        const on = (b.getAttribute('data-col-short') === '1') === state.short;
        b.classList.toggle('on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    saveSetting('short', state.short ? 1 : 0);
    if (!quiet) { funnel('setting', { k: 'short', v: state.short ? 1 : 0 }); recompute(); }
}
function setSchool(id, quiet) {
    if (!SCHOOLS[id]) return;
    // Пример читается ПРЕЖНЕЙ записью: «1.234» под мексиканской — 1,234, под
    // испанской — 1234. Смена записи не должна молча менять число.
    const before = parseExample($('colExpr').value, numCtx());
    state.school = id;
    $('colSchool').value = id;
    const east = SCHOOLS[id].digits === 'east';
    $('colEastWrap').hidden = !east;
    if (east) $('colEast').checked = state.east == null ? true : state.east;
    // Помнится только ВЫБОР человека: запись по умолчанию своего языка не
    // сохраняем, иначе один визит на испанскую страницу навсегда ставил бы
    // мексиканскую запись и на немецкой.
    if (!quiet) saveSetting('school', id);
    // Знак действия и десятичный знак в поле — по школе: «:» или «÷», «×» или
    // «·», «12,5» или «12.5». Значения чисел те же.
    if (before && !before.error && before.nums && before.nums.length > 1 && !before.open) {
        const nums = before.nums.map((d) => d.i + (d.f ? '.' + d.f : ''));
        $('colExpr').value = srcText(nums, before.op);
    }
    relabelSamples();
    if (!quiet) { funnel('setting', { k: 'school', v: id }); recompute(); }
}

/** Чипы-примеры пишутся знаками выбранной записи (pt: «84 ÷ 4», es: «12.5 ÷ 0.25»). */
function relabelSamples() {
    document.querySelectorAll('[data-col-sample]').forEach((c) => {
        c.textContent = srcText(sampleParts(c.getAttribute('data-col-sample')), c.getAttribute('data-col-sample-op'));
    });
    // Кнопка деления и пример в подсказке поля («por ejemplo, 1234 ÷ 56») — тем же знаком, что на чипах.
    const divBtn = document.querySelector('[data-col-op="div"]');
    if (divBtn) divBtn.textContent = opSym('div', currentSchool());
    const expr = $('colExpr');
    const ph = expr && expr.getAttribute('placeholder');
    if (ph) expr.setAttribute('placeholder', ph.replace(/(\d)\s*[÷:]\s*(\d)/, '$1 ' + opSym('div', currentSchool()) + ' $2'));
    paintRecent();
}

/* ── клавиатура ───────────────────────────────────────────────────────── */
function onKey(e) {
    const tag = (e.target && e.target.tagName) || '';
    const typing = /^(INPUT|SELECT|TEXTAREA)$/.test(tag) || (e.target && e.target.isContentEditable);
    const inApp = $('colApp').contains(e.target) || e.target === document.body;
    if (!inApp || typing || e.altKey || e.ctrlKey || e.metaKey) return;
    if (state.view === 'practice') {
        if (!$('colSheetWrap').contains(e.target) && !$('colPad').contains(e.target)) return;
        if (/^[0-9]$/.test(e.key)) { typeDigit(e.key); e.preventDefault(); return; }
        if (/^[٠-٩]$/.test(e.key)) { typeDigit(String('٠١٢٣٤٥٦٧٨٩'.indexOf(e.key))); e.preventDefault(); return; }
        if (e.key === 'Backspace' || e.key === 'Delete') { erase(); e.preventDefault(); return; }
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { moveCur(1); e.preventDefault(); return; }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { moveCur(-1); e.preventDefault(); return; }
        if (e.key === 'Enter') { checkPractice(); e.preventDefault(); }
        return;
    }
    if (!state.sheet) return;
    const fwd = RTL ? 'ArrowLeft' : 'ArrowRight';
    const back = RTL ? 'ArrowRight' : 'ArrowLeft';
    if (e.key === fwd) { go(state.step + 1); e.preventDefault(); }
    else if (e.key === back) { go(state.step - 1); e.preventDefault(); }
    else if (e.key === 'Home') { go(0); e.preventDefault(); }
    else if (e.key === 'End') { go(state.sheet.steps.length - 1); e.preventDefault(); }
}

/* ── запуск ───────────────────────────────────────────────────────────── */
function init() {
    const expr = $('colExpr');
    if (!expr) return;

    const s0 = loadSetting('school', null);
    if (s0 && SCHOOLS[s0]) state.school = s0;
    state.divMode = loadSetting('divMode', 'rem') === 'dec' ? 'dec' : 'rem';
    state.short = +loadSetting('short', 0) === 1;
    const pl = loadSetting('places', 'auto');
    state.places = /^\d$/.test(String(pl)) ? +pl : 'auto';
    const fromHash = loadHash();

    $('colSchool').value = state.school;
    setSchool(state.school, true);
    setDivMode(state.divMode, true);
    setShort(state.short, true);
    $('colPlaces').value = String(state.places);
    $('colPInstant').checked = +loadSetting('instant', 1) === 1;
    if (!fromHash) expr.value = srcText(['1234', '56'], 'div');

    expr.addEventListener('input', () => schedule('typed'));
    expr.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        clearTimeout(timer);
        recompute({ from: 'enter' });
        // Enter на телефоне — «Готово»: клавиатура уходит и открывает запись под полем.
        try { if (window.matchMedia('(pointer:coarse)').matches) expr.blur(); } catch (_) { /* старый браузер */ }
    });
    $('colAgain').addEventListener('click', again);
    loadRecent();
    paintRecent();
    $('colRecent').addEventListener('click', (e) => {
        const b = e.target.closest('[data-col-recent]');
        if (b) {
            $('colExpr').value = srcText(sampleParts(b.getAttribute('data-col-recent')), b.getAttribute('data-col-recent-op'));
            const m = b.getAttribute('data-col-recent-mode');
            if (m) setDivMode(m, true);
            funnel('recent', { op: b.getAttribute('data-col-recent-op') });
            recompute({ from: 'recent' });
            return;
        }
        if (e.target.closest('#colRecentClear')) { recent = []; saveSetting('recent', recent); paintRecent(); }
    });
    document.querySelectorAll('[data-col-op]').forEach((b) => b.addEventListener('click', () => pickOp(b.getAttribute('data-col-op'))));
    document.querySelectorAll('[data-col-sample]').forEach((c) => c.addEventListener('click', () => applySample(c)));
    $('colAddNum').addEventListener('click', () => {
        const box = $('colNums');
        const S = currentSchool();
        const n = box.querySelectorAll('.col-num').length;
        if (n >= 5) return;
        $('colExpr').value = $('colExpr').value.trim() + ' ' + opSym('add', S) + ' ';
        recompute({});
        const all = box.querySelectorAll('.col-num');
        if (all[all.length - 1]) all[all.length - 1].focus();
    });
    $('colSchool').addEventListener('change', () => setSchool($('colSchool').value));
    $('colEast').addEventListener('change', () => { state.east = $('colEast').checked; saveSetting('east', state.east ? 1 : 0); funnel('setting', { k: 'east', v: state.east ? 1 : 0 }); relabelSamples(); recompute(); });
    document.querySelectorAll('[data-col-mode]').forEach((b) => b.addEventListener('click', () => setDivMode(b.getAttribute('data-col-mode'))));
    document.querySelectorAll('[data-col-short]').forEach((b) => b.addEventListener('click', () => setShort(b.getAttribute('data-col-short') === '1')));
    $('colPlaces').addEventListener('change', () => {
        const v = $('colPlaces').value;
        state.places = v === 'auto' ? 'auto' : +v;
        saveSetting('places', v);
        funnel('setting', { k: 'places', v });
        recompute();
    });
    $('colMore').addEventListener('click', () => { setDivMode('dec'); });

    $('colFirst').addEventListener('click', () => go(0));
    $('colPrev').addEventListener('click', () => go(state.step - 1));
    $('colNext').addEventListener('click', () => go(state.step + 1));
    $('colAll').addEventListener('click', () => go(state.sheet ? state.sheet.steps.length - 1 : 0));

    $('colCopy').addEventListener('click', doCopy);
    $('colPng').addEventListener('click', doPng);
    $('colPdf').addEventListener('click', doPdf);
    $('colPrint').addEventListener('click', doPrint);
    $('colShare').addEventListener('click', doShare);
    $('colCheckBtn').addEventListener('click', showCheck);

    document.querySelectorAll('[data-col-view]').forEach((b) => b.addEventListener('click', () => setView(b.getAttribute('data-col-view'))));
    $('colViews').addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        const tabs = Array.from(document.querySelectorAll('[data-col-view]'));
        const i = tabs.findIndex((x) => x.classList.contains('on'));
        const n = tabs[(i + 1) % tabs.length];
        setView(n.getAttribute('data-col-view'));
        n.focus();
        e.preventDefault();
    });
    document.querySelectorAll('[data-col-digit]').forEach((b) => b.addEventListener('click', () => { typeDigit(b.getAttribute('data-col-digit')); }));
    $('colPErase').addEventListener('click', erase);
    $('colPPrev').addEventListener('click', () => moveCur(-1));
    $('colPNextCell').addEventListener('click', () => moveCur(1));
    $('colPCheck').addEventListener('click', checkPractice);
    $('colPHint').addEventListener('click', hint);
    $('colPShow').addEventListener('click', () => { funnel('practice_show', {}); setView('solve'); });
    $('colPNew').addEventListener('click', newExample);
    $('colPInstant').addEventListener('change', () => saveSetting('instant', $('colPInstant').checked ? 1 : 0));
    ['colPOp', 'colPLevel'].forEach((id) => {
        const el = $(id);
        const v = loadSetting(id, null);
        if (v != null && Array.from(el.options).some((o) => o.value === String(v))) el.value = String(v);
        el.addEventListener('change', () => saveSetting(id, el.value));
    });
    $('colWsMake').addEventListener('click', makeWorksheet);

    document.addEventListener('keydown', onKey);
    recompute({});

    // Опора для стендов: состояние видно снаружи без чтения пикселей.
    window.__colState = state;
    window.__colApi = { recompute, go, typeDigit, hint, checkPractice, setView, solutionText, sheetText: () => state.sheet && sheetText(state.sheet) };
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
